/**
 * The guided tour of the demo ("Kennenlernmodus"), one per demo persona.
 *
 * Web and app walk the same steps (filtered by `platforms`) through the same
 * demo data: the demo club Musterstadt, its D-EXAA with the cluster of known
 * damages on the left wing, and the unreviewed report d-0003. Every step names
 * an anchor — `data-tour="…"` on the web, `<TourTarget id="…">` in the app —
 * and a guard test per client proves each anchor exists on the screen the step
 * targets (apps/web/src/test/tour.test.tsx, apps/mobile/src/tour/anchors.test.ts).
 *
 * The tour only runs in the demo, never in a real session, and there is no
 * platform persona: the platform area is not part of the demo.
 *
 * Texts use the help's placeholders (src/help/facts.ts), so a renamed button is
 * named correctly here too; help.lock.json covers them like help topics.
 */
import type { MemberRole } from '@fsv/core';
import type { DemoRole } from '../data/demoPersonas';

export type TourPersona = Extract<DemoRole, 'member' | 'editor' | 'admin'>;
export const TOUR_PERSONAS: readonly TourPersona[] = ['member', 'editor', 'admin'];

export type TourPlatform = 'web' | 'app';

/** Where an anchor lives. The guard tests map a surface to a route (web) or source files (app). */
export type TourSurface =
  | 'shell'
  | 'fleet'
  | 'plan'
  | 'report'
  | 'inbox'
  | 'detail'
  | 'search'
  | 'profile'
  | 'admin-members'
  | 'admin-fleet'
  | 'admin-settings'
  | 'admin-club'
  | 'admin-trash';

export interface TourStep {
  /** Stable within its tour: progress is stored by index, and the lock by id. */
  readonly id: string;
  readonly anchor: string;
  readonly surface: TourSurface;
  readonly title: string;
  readonly body: string;
  /** Absent: both. */
  readonly platforms?: readonly TourPlatform[];
  /** Web: path below /v/<demo club>, opened when the step starts away from it. '' is the fleet. */
  readonly webPath?: string;
  /** App: expo-router path, opened the same way. */
  readonly appPath?: string;
  /**
   * The person does something here (tap the drawing, open a report). The step
   * says what, and the tour moves on by itself once this anchor appears.
   */
  readonly advanceOn?: string;
}

/** The demo's aircraft with known damages on the left wing, and its unreviewed report. */
export const TOUR_AIRCRAFT = 'D-EXAA';
export const TOUR_REPORT_ID = 'd-0003';

const help: TourStep = {
  id: 'help',
  anchor: 'help',
  surface: 'shell',
  title: '{{tx.help.title}}',
  body: 'Über das Fragezeichen kommst du jederzeit zur passenden Stelle der Hilfe – auch ohne Netz. Dort kannst du die Tour auch neu starten. Viel Spaß beim Ausprobieren!',
};

const member: readonly TourStep[] = [
  {
    id: 'fleet',
    anchor: 'fleet-card',
    surface: 'fleet',
    webPath: '',
    appPath: '/',
    title: 'Deine Flotte',
    body: 'Jede Karte ist ein Flugzeug des Vereins mit seinen offenen Schäden. Tippe auf {{tour.aircraft}}, um den Lageplan zu öffnen.',
    advanceOn: 'plan-board',
  },
  {
    id: 'views',
    anchor: 'plan-views',
    surface: 'plan',
    webPath: '/flugzeug/D-EXAA/plan',
    appPath: '/plan/D-EXAA',
    title: 'Sechs Ansichten',
    body: 'Von {{view.oben}} bis {{view.vorne}} und {{view.innen}} für die Kabine: Wähle die Seite, auf der du den Schaden siehst.',
  },
  {
    id: 'board',
    anchor: 'plan-board',
    surface: 'plan',
    webPath: '/flugzeug/D-EXAA/plan',
    appPath: '/plan/D-EXAA',
    title: 'Stelle antippen',
    body: 'Jeder Punkt ist ein bekannter Schaden. Tippe knapp neben die Punkte am linken Flügel auf eine freie Stelle – am Handy zeigt eine Lupe, wo das Kreuz landet, wenn du den Finger kurz gedrückt hältst. Dann „{{tx.plan.placeConfirm}}“: Die App zeigt dir, was in der Nähe schon erfasst ist. Wird es eng, zoome vorher mit zwei Fingern heran.',
    advanceOn: 'report-sheet',
  },
  {
    id: 'nearby',
    anchor: 'report-nearby',
    surface: 'report',
    title: '{{tx.create.nearbyTitle}}',
    body: 'Bevor du etwas einträgst, zeigt dir die App, was in der Nähe schon bekannt ist. Ist es derselbe Schaden, öffnest du ihn und schreibst höchstens eine Rückfrage/einen Hinweis. Sonst: „{{tx.create.newAnyway}}“.',
  },
  {
    id: 'photos',
    anchor: 'report-photos',
    surface: 'report',
    title: '{{de.damage.photos}}',
    body: 'Bis zu {{limits.maxPhotosPerDamage}} Fotos: am besten eines aus 1–2 m Abstand und eines aus der Nähe. Standortdaten entfernt die App.',
  },
  {
    id: 'submit',
    anchor: 'report-submit',
    surface: 'report',
    title: '{{tx.create.submitMember}}',
    body: '{{tx.create.memberStatusNote}} In der Demo bleibt alles auf diesem Gerät.',
  },
  help,
];

const editor: readonly TourStep[] = [
  {
    id: 'inbox-nav',
    anchor: 'nav-inbox',
    surface: 'shell',
    platforms: ['web'],
    webPath: '',
    title: '{{de.nav.inbox}}',
    body: 'Hier landen die Meldungen der Mitglieder. Die Zahl zeigt, wie viele noch nicht gesichtet sind.',
  },
  {
    id: 'inbox',
    anchor: 'inbox-list',
    surface: 'inbox',
    webPath: '/eingang',
    appPath: '/inbox',
    title: 'Meldungen sichten',
    body: 'Die älteste Meldung steht oben, mit Position, Melder, Alter und den Schäden in der Nähe. Nach {{settings.reviewDueDays}} Tagen ist sie „{{de.review.overdue}}“.',
  },
  {
    id: 'inbox-actions',
    anchor: 'inbox-actions',
    surface: 'inbox',
    platforms: ['web'],
    webPath: '/eingang',
    title: 'Drei Ausgänge',
    body: '„{{de.review.confirm}}“ übernimmt die Meldung, „{{de.review.merge}}“ hängt sie an einen bekannten Schaden, „{{de.review.reject}}“ verlangt eine Begründung, die der Melder sieht.',
  },
  {
    id: 'detail-actions',
    anchor: 'detail-actions',
    surface: 'detail',
    webPath: '/schaden/d-0003',
    appPath: '/damage/d-0003',
    title: '{{tx.detail.reviewActions}}',
    body: 'Im Schaden selbst stehen nur die Schritte, die vom aktuellen Status aus gehen – bis „{{status.behoben}}“. In der App sichtest du hier.',
  },
  {
    id: 'detail-history',
    anchor: 'detail-history',
    surface: 'detail',
    webPath: '/schaden/d-0003',
    appPath: '/damage/d-0003',
    title: '{{de.damage.history}}',
    body: 'Jede Änderung steht hier mit Person und Zeit. Nach jeder Aktion kannst du sie {{limits.undoSeconds}} Sekunden lang mit „{{de.damage.undo}}“ zurücknehmen.',
  },
  {
    id: 'search',
    anchor: 'search-input',
    surface: 'search',
    webPath: '/suche',
    appPath: '/search',
    title: '{{de.nav.search}}',
    body: 'Über alle Flugzeuge: Text, Kennzeichen oder Schadensnummer – „3“ genügt für die dritte.',
  },
  help,
];

const admin: readonly TourStep[] = [
  {
    id: 'admin-nav',
    anchor: 'nav-admin',
    surface: 'shell',
    platforms: ['web'],
    webPath: '',
    title: '{{de.nav.admin}}',
    body: 'Als {{role.admin}} kannst du alles, was die Technik kann, und verwaltest hier den Verein.',
  },
  {
    id: 'admin-app',
    anchor: 'profile-admin',
    surface: 'profile',
    platforms: ['app'],
    appPath: '/profile',
    title: '{{de.nav.admin}}',
    body: '{{mt.web.hint}} Sichten und Status pflegen geht auch hier in der App.',
  },
  {
    id: 'members',
    anchor: 'admin-members',
    surface: 'admin-members',
    platforms: ['web'],
    webPath: '/admin/mitglieder',
    title: '{{de.admin.members}}',
    body: 'Nur wer hier aktiv eingetragen ist, kann sich anmelden. Einzeln einladen oder als CSV importieren, mit Vorschau.',
  },
  {
    id: 'fleet',
    anchor: 'admin-fleet',
    surface: 'admin-fleet',
    platforms: ['web'],
    webPath: '/admin/flotte',
    title: '{{de.admin.fleet}}',
    body: 'Flugzeuge anlegen oder außer Dienst stellen, eigene Muster mit Risszeichnungen hochladen.',
  },
  {
    id: 'participation',
    anchor: 'admin-participation',
    surface: 'admin-settings',
    platforms: ['web'],
    webPath: '/admin/einstellungen',
    title: '{{de.participation.title}}',
    body: '„{{de.participation.level.report}}“, „{{de.participation.level.comment}}“ oder „{{de.participation.level.read}}“: Du entscheidest, was Mitglieder schreiben. Technik ist nie eingeschränkt.',
  },
  {
    id: 'export',
    anchor: 'admin-export',
    surface: 'admin-club',
    platforms: ['web'],
    webPath: '/admin/verein',
    title: '{{tx.export.title}}',
    body: 'Alle Daten des Vereins als ZIP – für die Datenübertragbarkeit oder wenn der Verein umzieht.',
  },
  {
    id: 'trash',
    anchor: 'admin-trash',
    surface: 'admin-trash',
    platforms: ['web'],
    webPath: '/admin/papierkorb',
    title: '{{de.admin.trash}}',
    body: 'Gelöscht wird nie endgültig: Hier holst du Schäden, Fotos, Rückfragen/Hinweise und Flugzeuge zurück.',
  },
  {
    id: 'inbox',
    anchor: 'inbox-list',
    surface: 'inbox',
    platforms: ['app'],
    appPath: '/inbox',
    title: '{{de.nav.inbox}}',
    body: 'Neue Meldungen der Mitglieder sichtest du auch unterwegs.',
  },
  help,
];

export const TOURS: Readonly<Record<TourPersona, readonly TourStep[]>> = { member, editor, admin };

/** The steps of one persona on one platform, in order. */
export function tourSteps(persona: TourPersona, platform: TourPlatform): readonly TourStep[] {
  return TOURS[persona].filter((s) => !s.platforms || s.platforms.includes(platform));
}

export function isTourPersona(value: unknown): value is TourPersona {
  return typeof value === 'string' && (TOUR_PERSONAS as readonly string[]).includes(value);
}

/**
 * Whose tour runs, if any: only a demo session on the demo backend, and only
 * for a persona that has a tour. Both checks, like the demo band: a real
 * session never gets a tour, whatever a stored persona says.
 */
export function tourPersonaOf(session: { mode: 'api' | 'demo'; demoRole?: string | undefined } | null, backendKind: 'api' | 'demo'): TourPersona | null {
  if (!session || session.mode !== 'demo' || backendKind !== 'demo') return null;
  return isTourPersona(session.demoRole) ? session.demoRole : null;
}

/**
 * The persona whose tour a real person gets from the welcome (0015): their
 * highest role in any of their clubs — an admin sees what admins do. Somebody
 * without a membership (a platform manager only) sees the member's tour.
 */
export function welcomePersona(roles: readonly MemberRole[]): TourPersona {
  if (roles.includes('admin')) return 'admin';
  if (roles.includes('editor')) return 'editor';
  return 'member';
}

/**
 * Progress of one persona's tour, kept on the device (localStorage /
 * AsyncStorage) — never on a server, the demo has none.
 *   running  the tour is open at `step`
 *   paused   closed half way; offered as "fortsetzen"
 *   done     finished or ended; never starts by itself again
 */
export interface TourProgress {
  readonly status: 'running' | 'paused' | 'done';
  readonly step: number;
}

/** Storage key per persona (both clients). */
export const tourStorageKey = (persona: TourPersona) => `fsv.tour.v1.${persona}`;

/** Parses a stored value; anything unreadable counts as "never started". */
export function parseTourProgress(raw: string | null): TourProgress | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<TourProgress>;
    if ((v.status === 'running' || v.status === 'paused' || v.status === 'done') && typeof v.step === 'number' && v.step >= 0) {
      return { status: v.status, step: Math.floor(v.step) };
    }
  } catch {
    /* fall through */
  }
  return null;
}

/** Starts by itself only the very first time a persona is opened. */
export function shouldAutoStart(progress: TourProgress | null): boolean {
  return progress === null;
}

/** Where a resumed tour opens: the stored step, clamped to the tour's length. */
export function resumeStep(progress: TourProgress | null, count: number): number {
  if (!progress || progress.status === 'done') return 0;
  return Math.min(Math.max(0, progress.step), Math.max(0, count - 1));
}
